import {useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import HeadingAndExpandableBody from '../../UI/HeadingAndExpandableBody';
import {Context} from '../../../context';
import Button from '../../UI/Button';
import ErrorNote from '../../UI/ErrorNote';

import {getDateString, dollarAmount} from '../../../helperFunctions';

import {CSVLink} from 'react-csv';
import getAccountReportInfo from './getAccountReport';

function AccountReport(props) {
  const [filteredOrderHistory, setFilteredOrderHistory] = useState([]);
  const [filteredTransitCards, setFilteredTransitCards] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [error, setError] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const {localeText, getTicketName, account, locale} = useContext(Context);

  const [headers, setHeaders] = useState([]);

  useEffect(() => {
    setHeaders([
      {label: localeText('rw_title_category', 'Category'), key: 'category'},
      {label: localeText('rw_title_date', 'Date'), key: 'date'},
      {
        label: localeText('rw_title_order_number', 'Order Number'),
        key: 'orderId'
      },
      {
        label: localeText('rw_title_description', 'Description'),
        key: 'description'
      },
      {
        label: localeText('rw_title_payment_method', 'Payment Method'),
        key: 'paymentMethod'
      },
      {label: localeText('rw_title_amount', 'Amount'), key: 'amount'},
      {label: localeText('rw_title_tap', 'Tap'), key: 'tap'},
      {label: localeText('rw_title_location', 'Location'), key: 'stationName'},
      {
        label: localeText('rw_title_transit_card_nickname', 'Card Name'),
        key: 'cardName'
      },
      {
        label: localeText('rw_title_card_number', 'Card Number'),
        key: 'cardNumber'
      },
      {
        label: localeText('rw_title_stored_value_used', 'Stored Value Used'),
        key: 'storedValueUsed'
      }
    ]);
  }, [locale]);

  const navigate = useNavigate();
  const navigateToAccountReportPdf = () => {
    if (startDate && endDate) {
      navigate(
        `/myaccount/account-report?start-date=${startDate}&end-date=${endDate}`
      );
    } else {
      setError(
        localeText('rw_msg_set_date_range', 'Please set date range to proceed')
      );
    }
  };

  const processData = () => {
    if (startDate && endDate) {
      setError(null);

      const csvOrderHistory = [];
      for (const order of filteredOrderHistory) {
        const csvOrder = {};
        csvOrder.category = localeText('rw_title_order', 'Order');
        csvOrder.orderId = order.orderId;
        csvOrder.description = getTicketName(order.productId);
        csvOrder.date = getDateString(order.dateTime);
        csvOrder.paymentMethod =
          order.cardType && order.cardNumber
            ? `${order.cardType.replace('Credit', '')} ****${order.cardNumber}`
            : '';
        csvOrder.amount = dollarAmount(order.amount);

        csvOrderHistory.push(csvOrder);
      }

      const csvScans = [];
      for (const scan of filteredTransitCards) {
        const newScan = {...scan};
        newScan.category = localeText('rw_title_transaction', 'Transaction');
        newScan.tap = getTicketName(scan.productId);
        newScan.cardName = scan.nickname;
        newScan.cardNumber = scan.cardNumber;
        newScan.date = getDateString(scan.created);
        newScan.storedValueUsed = newScan.tripCost
          ? dollarAmount(newScan.tripCost)
          : null;
        csvScans.push(newScan);
      }
      setCsvData([...csvOrderHistory, ...csvScans]);
    } else {
      setError(
        localeText('rw_msg_set_date_range', 'Please set date range to proceed')
      );
      return false;
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      const {orderHistory, scans} = getAccountReportInfo({
        startDate,
        endDate,
        account
      });

      setFilteredOrderHistory(orderHistory);
      setFilteredTransitCards(scans);
    } else {
      setFilteredOrderHistory([]);
      setFilteredTransitCards([]);
    }
  }, [account, startDate, endDate]);

  return (
    <HeadingAndExpandableBody
      headingAndExpandableBodyId='account-report'
      heading={localeText('rw_header_account_report', 'Account Report')}
      expanded={props.expanded}
    >
      <div className='flex flex-col sm:flex-row p-2 gap-4'>
        <div className='flex flex-col gap-5'>
          <div className='flex justify-between gap-4 items-center'>
            <div className='font-semibold'>
              {localeText('rw_title_start_date', 'Start Date')}
            </div>
            <input
              type='date'
              id='start-date'
              title='start-date'
              className='border border-gray-400 rounded-md p-1 w-32'
              max={new Date().toISOString().split('T')[0]}
              onChange={() => {
                const inputStartDate = document.getElementById('start-date');
                const inputEndDate = document.getElementById('end-date');
                inputEndDate.min = inputStartDate.value;
                setStartDate(inputStartDate.value);
              }}
            />
          </div>
          <div className='flex justify-between gap-4 items-center'>
            <div className='font-semibold'>
              {localeText('rw_title_end_date', 'End Date')}
            </div>
            <input
              type='date'
              id='end-date'
              title='end-date'
              className='border border-gray-400 rounded-md p-1 w-32'
              max={new Date().toISOString().split('T')[0]}
              onChange={() => {
                const inputStartDate = document.getElementById('start-date');
                const inputEndDate = document.getElementById('end-date');
                inputStartDate.max = inputEndDate.value;
                setEndDate(inputEndDate.value);
              }}
            />
          </div>
        </div>
        <div className='flex flex-col justify-between gap-2'>
          <div className='flex flex-row gap-4'>
            <CSVLink
              filename={'account-report.csv'}
              headers={headers}
              data={csvData}
              onClick={processData}
            >
              <Button>
                {localeText('rw_button_save_to_csv', 'Save to CSV')}
              </Button>
            </CSVLink>
            <Button onClick={navigateToAccountReportPdf}>
              {localeText('rw_button_save_to_pdf', 'Save to PDF')}
            </Button>
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
        </div>
      </div>
    </HeadingAndExpandableBody>
  );
}

export default AccountReport;
